/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {EsciService} from '../../service/esci.service';
import {EsciMessageService} from '../../shared/esci-message.service';
import {ColDef, GridApi, GridReadyEvent} from 'ag-grid-community';

@Component({
    selector: 'app-terminal-release-tool-relationship-dialog',
    template: `
        <div class="relationship-dialog-container">
          <div class="relationship-info mb-3">
            <strong>Terminal Release Tool:</strong> {{terminalReleaseTool?.partNumber}} (ID: {{terminalReleaseTool?.id}})
          </div>

          <div class="relationship-grid-section">
            <h4>Associated Kits</h4>
            <ag-grid-angular
              class="ag-theme-alpine relationship-grid"
              [rowData]="kitLinks"
              [columnDefs]="kitColumnDefs"
              [defaultColDef]="defaultColDef"
              [rowSelection]="'single'"
              (gridReady)="onGridReady($event)"
              [domLayout]="'autoHeight'">
            </ag-grid-angular>
            @if (kitLinks.length === 0) {
              <div class="no-kits-message">
                No kit relationships found for this terminal release tool.
              </div>
            }
          </div>

          @if (hasAdminPermission) {
            <div class="add-relationship-section mt-4">
              <h4>Add New Kit Relationship</h4>
              <div class="p-fluid grid">
                <div class="col-8">
                  <p-select
                    [options]="availableKits"
                    [(ngModel)]="selectedKit"
                    optionLabel="terminalReleaseToolKit"
                    placeholder="Select a Kit"
                    [filter]="true"
                    filterBy="terminalReleaseToolKit"
                    appendTo="body">
                  </p-select>
                </div>
                <div class="col-4">
                  <button pButton gmPrimary label="Add Relationship"
                    [disabled]="!selectedKit"
                    [severity]="!selectedKit ? 'secondary' : null"
                  (click)="addRelationship()"></button>
                </div>
              </div>
            </div>
          }

          <div class="flex justify-content-end gap-2 mt-4">
            <button pButton gmSecondary label="Close" (click)="close()"></button>
          </div>
        </div>
        `,
    styles: [`
        .relationship-dialog-container {
            padding: 0.5rem;
        }
        .relationship-grid {
            width: 100%;
            height: 200px;
        }
        .relationship-info {
            padding: 0.5rem;
            background-color: #f8f9fa;
            border-radius: 4px;
            font-size: 0.875rem;
        }
        .no-kits-message {
            padding: 0.75rem;
            text-align: center;
            color: #6c757d;
            font-style: italic;
        }
        h4 {
            font-size: 0.95rem;
            margin-bottom: 0.5rem;
        }
    `],
    standalone: false
})
export class TerminalReleaseToolRelationshipDialogComponent implements OnInit {
    terminalReleaseTool: any;
    hasAdminPermission = false;
    kitLinks: any[] = [];
    availableKits: any[] = [];
    selectedKit: any;
    private gridApi: GridApi;

    kitColumnDefs: ColDef[] = [
        {
            headerName: '',
            width: 45,
            suppressSizeToFit: true,
            cellRenderer: (params: any) => {
                if (!this.hasAdminPermission) {
                    return '';
                }
                const button = document.createElement('button');
                button.innerHTML = '<i class="pi pi-trash" style="font-size: 0.7rem;"></i>';
                button.className = 'p-button p-button-text p-button-sm p-button-danger';
                button.style.cssText = 'cursor: pointer; padding: 0.2rem 0.4rem; min-width: unset;';
                button.title = 'Delete';
                button.onclick = () => this.deleteRelationship(params.data);
                return button;
            }
        },
        {field: 'terminalReleaseToolKit.id', headerName: 'ID', width: 60},
        {field: 'terminalReleaseToolKit.terminalReleaseToolKit', headerName: 'Kit Name', flex: 1}
    ];

    defaultColDef: ColDef = {
        sortable: true,
        filter: true,
        resizable: true
    };

    constructor(
        public config: DynamicDialogConfig,
        private dialogRef: DynamicDialogRef,
        private esciService: EsciService,
        private messageService: EsciMessageService
    ) {
        // Support both 'terminalReleaseTool' (from grid) and 'componentData' (from detail view)
        this.terminalReleaseTool = config.data.terminalReleaseTool || config.data.componentData;
        this.hasAdminPermission = config.data.hasAdminPermission;
    }

    ngOnInit(): void {
        this.loadKitRelationships();
        if (this.hasAdminPermission) {
            this.loadAvailableKits();
        }
    }

    onGridReady(params: GridReadyEvent): void {
        this.gridApi = params.api;
    }

    loadKitRelationships(): void {
        if (!this.terminalReleaseTool?.id) {
            return;
        }
        // Call the backend to get kit relationships for this TerminalReleaseTool
        this.esciService.getTerminalReleaseToolLinks(this.terminalReleaseTool.id).subscribe({
            next: (response: any) => {
                this.kitLinks = response?.results || [];
            },
            error: (err: any) => {
                console.error('Error loading kit relationships:', err);
                this.messageService.processError(err?.error?.message || err?.message || 'Error loading relationships');
            }
        });
    }

    loadAvailableKits(): void {
        // Load all available TerminalReleaseToolKits for the dropdown
        this.esciService.getObjects('TerminalReleaseToolKit').subscribe({
            next: (response: any) => {
                this.availableKits = response?.results || [];
            },
            error: (err: any) => {
                console.error('Error loading available kits:', err);
            }
        });
    }

    addRelationship(): void {
        if (!this.selectedKit || !this.terminalReleaseTool?.id) {
            return;
        }

        // Construct the link object as expected by the backend
        const newLink = {
            terminalReleaseTool: {
                id: this.terminalReleaseTool.id,
                partNumber: this.terminalReleaseTool.partNumber
            },
            terminalReleaseToolKit: {
                id: this.selectedKit.id,
                terminalReleaseToolKit: this.selectedKit.terminalReleaseToolKit
            }
        };

        this.esciService.createTerminalReleaseToolLink(newLink).subscribe({
            next: () => {
                this.messageService.processSuccess('Relationship added successfully');
                this.loadKitRelationships();
                this.selectedKit = null;
            },
            error: (err: any) => {
                console.error('Error adding relationship:', err);
                this.messageService.processError(err?.error?.message || err?.message || 'Error adding relationship');
            }
        });
    }

    deleteRelationship(link: any): void {
        if (!confirm('Are you sure you want to delete this relationship?')) {
            return;
        }

        this.esciService.deleteTerminalReleaseToolLink(link).subscribe({
            next: () => {
                this.messageService.processSuccess('Relationship deleted successfully');
                this.loadKitRelationships();
            },
            error: (err: any) => {
                console.error('Error deleting relationship:', err);
                this.messageService.processError(err?.error?.message || err?.message || 'Error deleting relationship');
            }
        });
    }

    close(): void {
        this.dialogRef.close();
    }
}


